import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, Globe, ShieldCheck, ChevronDown, Search, X, MapPin, Phone, Mail, ClipboardList } from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { TRANSLATIONS } from '../constants';

export const Layout = ({ children }: { children: React.ReactNode }) => {
  const { cart, language, setLanguage, categories, searchQuery, setSearchQuery } = useStore();
  const t = TRANSLATIONS;
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const cartCount = cart.reduce((acc, item) => acc + item.quantity, 0);

  // Logic to show max 3 categories directly, others in dropdown
  const visibleCategories = categories.slice(0, 3);
  const moreCategories = categories.slice(3);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 font-sans">
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm transition-all">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          
          {/* Logo & Mobile Menu Toggle */}
          <div className="flex items-center gap-3 lg:gap-8">
            <button 
              onClick={toggleMenu}
              className="md:hidden p-2 -ml-2 text-gray-600 hover:text-emerald-600 focus:outline-none rounded-lg active:bg-gray-100"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            <Link to="/" className="text-xl md:text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent whitespace-nowrap" onClick={() => setSearchQuery('')}>
              Didohurtownia
            </Link>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex gap-4 lg:gap-6 items-center">
               <Link 
                 to="/" 
                 className={`text-sm font-medium hover:text-emerald-600 transition-colors ${location.pathname === '/' ? 'text-emerald-600' : 'text-gray-600'}`}
                 onClick={() => setSearchQuery('')}
               >
                 {t.home[language]}
               </Link>
               
               {visibleCategories.map(cat => {
                 const categoryUrl = cat.slug ? `/category/${cat.slug}` : `/categories?cat=${cat.id}`;
                 const isActive = cat.slug 
                   ? location.pathname.includes(`/category/${cat.slug}`)
                   : location.search.includes(`cat=${cat.id}`);
                 return (
                   <Link 
                     key={cat.id} 
                     to={categoryUrl}
                     className={`text-sm font-medium hover:text-emerald-600 transition-colors ${isActive ? 'text-emerald-600' : 'text-gray-600'}`}
                     onClick={() => setSearchQuery('')}
                   >
                     {cat.name[language]}
                   </Link>
                 );
               })}

              {moreCategories.length > 0 && (
                 <div className="relative group py-2">
                    <button className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-emerald-600">
                       {language === 'en' ? 'More' : 'Więcej'} <ChevronDown className="w-4 h-4" />
                    </button>
                    <div className="absolute top-full left-0 w-48 bg-white shadow-lg rounded-xl py-2 border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all transform translate-y-2 group-hover:translate-y-0 z-50">
                       {moreCategories.map(cat => {
                         const categoryUrl = cat.slug ? `/category/${cat.slug}` : `/categories?cat=${cat.id}`;
                         return (
                           <Link
                               key={cat.id}
                               to={categoryUrl}
                               className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-emerald-600"
                               onClick={() => setSearchQuery('')}
                           >
                               {cat.name[language]}
                           </Link>
                         );
                       })}
                    </div>
                 </div>
              )}
            </nav>
          </div>

          {/* Actions (Search, Language, Cart) */}
          <div className="flex items-center gap-1 md:gap-4">
            {/* Desktop Search */}
            <div className="hidden md:block relative">
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={language === 'en' ? "Search..." : "Szukaj..."}
                    className="pl-9 pr-4 py-2 bg-gray-100 border-none rounded-full text-sm focus:ring-2 focus:ring-emerald-500 outline-none w-32 lg:w-64 transition-all hover:bg-gray-200"
                />
                <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>

            {/* Mobile Search Toggle */}
            <button 
              className={`md:hidden p-2 rounded-lg ${isSearchOpen ? 'bg-gray-100 text-emerald-600' : 'text-gray-600'}`}
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              aria-label="Search"
            >
              <Search className="w-5 h-5" />
            </button>

            <button
              onClick={() => setLanguage(language === 'en' ? 'pl' : 'en')}
              className="hidden md:flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 transition-colors"
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase">{language}</span>
            </button>

            <Link to="/orders" className="relative p-2 text-gray-600 hover:text-emerald-600 transition-colors" onClick={() => setSearchQuery('')}>
              <ClipboardList className="w-5 h-5" />
            </Link>

            <Link to="/cart" className="relative p-2 text-gray-600 hover:text-emerald-600 transition-colors" onClick={() => setSearchQuery('')}>
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute top-0 right-0 bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Mobile Search Bar (Expandable) */}
        {isSearchOpen && (
          <div className="md:hidden px-4 pb-4 animate-in slide-in-from-top-2 fade-in duration-200">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={language === 'en' ? "Search products..." : "Szukaj produktów..."}
                className="w-full pl-10 pr-4 py-3 bg-gray-100 border-none rounded-xl text-base focus:ring-2 focus:ring-emerald-500 outline-none"
                autoFocus
              />
              <Search className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 w-full bg-white border-b border-gray-200 shadow-xl animate-in slide-in-from-top-5 fade-in duration-200 z-40 max-h-[calc(100vh-64px)] overflow-y-auto">
             <div className="flex flex-col p-4 space-y-4">
                <Link 
                  to="/" 
                  className={`px-4 py-3 rounded-lg ${location.pathname === '/' ? 'bg-emerald-50 text-emerald-600 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                  onClick={() => {
                    setIsMenuOpen(false);
                    setSearchQuery('');
                  }}
                >
                  {t.home[language]}
                </Link>
                <div className="space-y-2 pl-4 border-l-2 border-gray-100">
                    <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider">{t.categories[language]}</p>
                    {categories.map(cat => {
                      const categoryUrl = cat.slug ? `/category/${cat.slug}` : `/categories?cat=${cat.id}`;
                      const isActive = cat.slug 
                        ? location.pathname.includes(`/category/${cat.slug}`)
                        : location.search.includes(`cat=${cat.id}`);
                      return (
                        <Link 
                          key={cat.id} 
                          to={categoryUrl}
                          className={`block px-4 py-2 rounded-lg ${isActive ? 'text-emerald-600 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                          onClick={() => {
                            setIsMenuOpen(false);
                            setSearchQuery('');
                          }}
                        >
                          {cat.name[language]}
                        </Link>
                      );
                    })}
                </div>
                
                 <Link
                   to="/orders"
                   className={`px-4 py-3 rounded-lg ${location.pathname === '/orders' || location.pathname.startsWith('/orders/') ? 'bg-emerald-50 text-emerald-600 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                   onClick={() => {
                     setIsMenuOpen(false);
                     setSearchQuery('');
                   }}
                 >
                   {t.myOrders[language]}
                 </Link>

                 <hr className="border-gray-100" />

                 <button
                   onClick={() => {
                     setLanguage(language === 'en' ? 'pl' : 'en');
                     setIsMenuOpen(false);
                   }}
                   className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                 >
                   <Globe className="w-4 h-4" />
                   <span className="uppercase">{language}</span>
                 </button>
             </div>
          </div>
        )}
      </header>

      <main className="flex-1 container mx-auto px-4 py-6 md:py-8">
        {children}
      </main>

      {!location.pathname.startsWith('/admin') && (
        <footer className="bg-white border-t border-gray-200 py-8 mt-auto">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
              {/* Company Info */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">Didohurtownia</h3>
                <p className="text-sm text-gray-600">
                  {language === 'en' 
                    ? 'Your trusted wholesale partner for quality products.'
                    : 'Twój zaufany partner hurtowy dla produktów wysokiej jakości.'}
                </p>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  {language === 'en' ? 'Contact Us' : 'Skontaktuj się z nami'}
                </h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Międzynarodowa 9/6A<br />05-552 Jabłonowo, Polska</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    <a 
                      href="https://wa.me/48729484502" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:text-emerald-600 transition-colors"
                    >
                      WhatsApp: +48 729 484 502
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 flex-shrink-0" />
                    <a 
                      href="mailto:didohurtownia98@gmail.com" 
                      className="hover:text-emerald-600 transition-colors"
                    >
                      didohurtownia98@gmail.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">
                  {language === 'en' ? 'Quick Links' : 'Szybkie linki'}
                </h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>
                    <Link to="/" className="hover:text-emerald-600 transition-colors" onClick={() => setSearchQuery('')}>
                      {t.home[language]}
                    </Link>
                  </li>
                  <li>
                    <Link to="/categories" className="hover:text-emerald-600 transition-colors" onClick={() => setSearchQuery('')}>
                      {t.categories[language]}
                    </Link>
                  </li>
                  <li>
                    <Link to="/cart" className="hover:text-emerald-600 transition-colors" onClick={() => setSearchQuery('')}>
                      {t.cart[language]}
                    </Link>
                  </li>
                  <li>
                    <Link to="/orders" className="hover:text-emerald-600 transition-colors" onClick={() => setSearchQuery('')}>
                      {t.myOrders[language]}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6 text-center text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} Didohurtownia. {language === 'en' ? 'All rights reserved.' : 'Wszelkie prawa zastrzeżone.'}
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};